// @flow
import { getAuthToken, getUserData } from './user';
import api from '../api/review';
import { createSelector } from 'reselect';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';

/* Action Types */
export const SUBMIT_REVIEW_FAIL = 'la/ui/submitReview/FAIL';
export const SUBMIT_REVIEW_REQUEST = 'la/ui/submitReview/REQUEST';
export const SUBMIT_REVIEW_SUCCESS = 'la/ui/submitReview/SUCCESS';
export const FETCH_REVIEW_FAIL = 'la/ui/fetchReview/FAIL';
export const FETCH_REVIEW_REQUEST = 'la/ui/fetchReview/REQUEST';
export const FETCH_REVIEW_SUCCESS = 'la/ui/fetchReview/SUCCESS';
export const FETCH_COMMENTS_FAIL = 'la/ui/fetchReviewComments/FAIL';
export const FETCH_COMMENTS_REQUEST = 'la/ui/fetchReviewComments/REQUEST';
export const FETCH_COMMENTS_SUCCESS = 'la/ui/fetchReviewComments/SUCCESS';
export const RESET_REVIEW_PAGE = 'RESET_REVIEW_PAGE';

// reducer
const DEFAULT_STATE = {
    error: false,
    reviewData: [
        {
            catID: 0,
            lots: [],
            preventReview: false,
            reviewed: false,
        },
    ],
    submitted: false,
    success: false,
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case SUBMIT_REVIEW_REQUEST:
            return {
                ...state,
                submitted: true,
                success: false,
            };
        case SUBMIT_REVIEW_SUCCESS:
            return {
                ...state,
                submitted: false,
                success: true,
            };
        case SUBMIT_REVIEW_FAIL:
            return {
                ...state,
                error: true,
                submitted: false,
                success: false,
            };
        case FETCH_REVIEW_SUCCESS:
            return {
                ...state,
                reviewData: action.payload || {
                    catID: 0,
                    lots: [],
                    preventReview: true,
                    reviewed: false,
                },
            };
        case FETCH_COMMENTS_SUCCESS:
            return {
                ...state,
                reviewComments: action.payload,
            };
        case RESET_REVIEW_PAGE:
            return {
                ...DEFAULT_STATE,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const reviewSelector = (state: GlobalState) => state.review;

export const getReviewSubmitted = createSelector(reviewSelector, state => state.submitted);

export const getReviewSubmitSuccess = createSelector(reviewSelector, state => state.success);

export const getReviewError = createSelector(reviewSelector, state => state.error);

export const getReviewData = createSelector(reviewSelector, state =>
    state.reviewData && state.reviewData.length === 1 ? state.reviewData[0] : state.reviewData,
);

export const getReviewComments = createSelector(reviewSelector, state => state.reviewComments);

/* ACTION CREATORS */
const submitReviewFail = error => ({
    error: true,
    payload: error,
    type: SUBMIT_REVIEW_FAIL,
});

const submitReviewRequest = review => ({
    payload: {
        review,
    },
    type: SUBMIT_REVIEW_REQUEST,
});

const submitReviewSuccess = review => ({
    meta: {
        review,
    },
    payload: review,
    type: SUBMIT_REVIEW_SUCCESS,
});

const fetchReviewDataFail = error => ({
    error: true,
    payload: error,
    type: FETCH_REVIEW_FAIL,
});

const fetchReviewDataSuccess = catIDs => ({
    meta: {
        catIDs,
    },
    payload: catIDs,
    type: FETCH_REVIEW_SUCCESS,
});

const fetchReviewCommentsFail = error => ({
    error: true,
    payload: error,
    type: FETCH_COMMENTS_FAIL,
});

const fetchReviewCommentsRequest = reviewId => ({
    payload: {
        reviewId,
    },
    type: FETCH_COMMENTS_REQUEST,
});

const fetchReviewCommentsSuccess = payload => ({
    payload,
    type: FETCH_COMMENTS_SUCCESS,
});

export const resetReviewPage = () => ({
    type: RESET_REVIEW_PAGE,
});

export const submitReview = (review: any) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch(submitReviewRequest(review));
        await api.submitReview({
            authToken,
            deployment,
            review,
        });
        return dispatch(submitReviewSuccess(review));
    } catch (error) {
        dispatch(submitReviewFail(error));
    }
};

export const fetchReviewData = (catalogIds: number[]) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const userData = getUserData(state);
        dispatch({ type: FETCH_REVIEW_REQUEST });

        const response = await api.fetchReviewData({
            authToken,
            bidderId: userData.bidderId,
            catalogIds,
            deployment,
        });
        return dispatch(fetchReviewDataSuccess(response.payload));
    } catch (error) {
        dispatch(fetchReviewDataFail(error));
    }
};

export const fetchReviewComments = (reviewId: any) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch(fetchReviewCommentsRequest(reviewId));
        const response = await api.fetchReviewComments({
            authToken,
            deployment,
            reviewId,
        });
        return dispatch(fetchReviewCommentsSuccess(response.payload));
    } catch (error) {
        dispatch(fetchReviewCommentsFail(error));
    }
};
